'use client';

import { type CSSProperties, type MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { BREAKPOINTS } from '@/app/constants';
import { cn, TRANSPARENT_PIXEL_DATA_URI } from '@/app/utils';
import { getS1BannerAsset } from './s1-banner-assets';

/**
 * 视差整体只在桌面显示（调用方传 `sm:hidden`），但 `display: none` 不会阻止 `<img src>` 发请求，
 * 移动端原本会白下 5 张桌面素材（约 140 KB），还要和真正要显示的移动整图抢带宽。
 * 用 `<source media>` 让移动端选中 1×1 data URI，才真正不走网络。
 */
const MOBILE_BLANK_SOURCE_MEDIA = `(max-width: ${BREAKPOINTS.SM_MAX}px)`;

/** 背景素材自身比例（1672 × 292）。各层坐标均以该画布为基准换算成百分比。 */
const BANNER_RATIO = 1672 / 292;

/**
 * 层定义。shift 为鼠标推到边缘时的最大位移（px，按 1440 渲染宽估算）：
 * 背景微动 → 人物中等 → 气泡最大，形成景深。
 */
interface ParallaxLayer {
  key: string;
  src: string;
  /** 容器内定位，单位 %（top 允许负值：人物溢出卡片顶部） */
  left: number;
  top: number;
  width: number;
  shift: number;
  className?: string;
}

/**
 * 数组顺序即图层顺序：靠前的在下方。
 *
 * 各层坐标由「完整 banner 减去纯背景」的差分定位得出（基准画布 1672 × 292）：
 * 人物 IoU 0.864（scale 1.0，即设计稿原始尺寸）；hand-heart 取自干净残差块；
 * say-hi / wear-sunglasses 与人物重叠较多，取残差与模板匹配的最优值。
 */
const LAYERS: ParallaxLayer[] = [
  // 背景固定不动，作为视差的静止参考面
  { key: 'bg', src: getS1BannerAsset('background'), left: 0, top: 0, width: 100, shift: 0 },
  // hand-heart / say-hi 在人物之前渲染，因此压在人物图层下面
  {
    key: 'bubble-hand-heart',
    src: getS1BannerAsset('bubbleHandHeart'),
    // 累计右移 56px：82.71% + 56/1672
    left: 86.06,
    top: 32.53,
    // 气泡统一缩放至 80%：11.3% × 0.8
    width: 9.04,
    shift: 24,
  },
  {
    key: 'bubble-say-hi',
    src: getS1BannerAsset('bubbleSayHi'),
    left: 52.15,
    // 累计上移 24px：62.33% − 24/292
    top: 54.11,
    // 气泡统一缩放至 80%：7.9% × 0.8
    width: 6.32,
    shift: 22,
  },
  // 人物：left 866/1672，top 0（头部溢出卡片上沿），width 728/1672；底部超出容器由 overflow-hidden 裁掉
  { key: 'characters', src: getS1BannerAsset('characters'), left: 51.79, top: 0, width: 43.54, shift: 12 },
  // 浮在人物之上，位移幅度最大
  {
    key: 'bubble-wear-sunglasses',
    src: getS1BannerAsset('bubbleWearSunglasses'),
    // 累计右移 34px：69.56% + 34/1672
    left: 71.59,
    top: 67.13,
    // 气泡统一缩放至 80%：11.36% × 0.8
    width: 9.09,
    shift: 26,
  },
];

interface S1BannerParallaxProps {
  className?: string;
}

export const S1BannerParallax = ({ className }: S1BannerParallaxProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // 归一化鼠标偏移，范围 [-1, 1]，(0,0) 为容器中心
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(query.matches);
    const onChange = (e: MediaQueryListEvent) => setReduceMotion(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (reduceMotion) return;
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setOffset({
        x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
        y: ((e.clientY - rect.top) / rect.height) * 2 - 1,
      });
    },
    [reduceMotion],
  );

  // 移出 banner 即复位，避免停在偏移状态
  const handleMouseLeave = useCallback(() => setOffset({ x: 0, y: 0 }), []);

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full overflow-hidden [aspect-ratio:var(--s1-banner-aspect-ratio)]', className)}
      style={{ '--s1-banner-aspect-ratio': BANNER_RATIO } as CSSProperties}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {LAYERS.map((layer) => (
        // display: contents 让 picture 不参与布局，img 的绝对定位仍相对外层容器解析
        <picture key={layer.key} className="contents">
          <source media={MOBILE_BLANK_SOURCE_MEDIA} srcSet={TRANSPARENT_PIXEL_DATA_URI} />
          <img
            src={layer.src}
            alt=""
            className="pointer-events-none absolute select-none transition-transform duration-200 ease-out will-change-transform"
            style={{
              left: `${layer.left}%`,
              top: `${layer.top}%`,
              width: `${layer.width}%`,
              transform: `translate3d(${(-offset.x * layer.shift).toFixed(2)}px, ${(-offset.y * layer.shift).toFixed(2)}px, 0)`,
            }}
            loading="eager"
            decoding="async"
          />
        </picture>
      ))}
    </div>
  );
};
